/**
 * Feature Definition
 *
 * Defines a feature/capability available in Backstage with detection logic
 * and setup information.
 */
export interface FeatureDefinition {
  id: string;
  name: string;
  description: string;
  category: 'Integration' | 'Development' | 'Operations' | 'Quality';
  tabPath?: string; // Path to the tab (e.g., "/kubernetes")
  requiredAnnotations?: string[];
  /** At least one of these must be present (OR). Combined with requiredAnnotations (AND). */
  anyOfAnnotations?: string[];
  optionalAnnotations?: string[];
  setupGuideUrl?: string;
  exampleYaml?: string;
}

/**
 * All available features in the Backstage instance
 */
export const FEATURE_DEFINITIONS: FeatureDefinition[] = [
  // Integration Features
  {
    id: 'github-actions-cicd',
    name: 'CI/CD - GitHub Actions',
    description: 'View GitHub Actions workflow runs and build history',
    category: 'Integration',
    tabPath: '/ci-cd',
    requiredAnnotations: ['github.com/project-slug'],
    setupGuideUrl: '/docs/default/component/backstage/features/feature-discovery#cicd',
    exampleYaml: `metadata:
  annotations:
    github.com/project-slug: your-org/your-repo`,
  },
  {
    id: 'aws-codebuild-cicd',
    name: 'CI/CD - AWS CodeBuild',
    description: 'View AWS CodeBuild project builds and logs',
    category: 'Integration',
    tabPath: '/ci-cd',
    requiredAnnotations: ['aws.amazon.com/aws-codebuild-project-arn'],
    setupGuideUrl: '/docs/default/component/backstage/features/feature-discovery#cicd',
    exampleYaml: `metadata:
  annotations:
    aws.amazon.com/aws-codebuild-project-arn: arn:aws:codebuild:region:account:project/name`,
  },
  {
    id: 'aws-ecs',
    name: 'AWS ECS',
    description: 'View Amazon ECS services, tasks, and clusters',
    category: 'Integration',
    tabPath: '/aws/ecs',
    requiredAnnotations: ['aws.amazon.com/amazon-ecs-service-tags'],
    setupGuideUrl: '/docs/default/component/backstage/howto/ecs_setup_guide',
    exampleYaml: `metadata:
  annotations:
    aws.amazon.com/amazon-ecs-service-tags: tagKey1=value1,tagKey2=value2`,
  },
  {
    id: 'kubernetes',
    name: 'Kubernetes',
    description: 'View deployments, pods, and services in your cluster',
    category: 'Integration',
    tabPath: '/kubernetes',
    requiredAnnotations: ['backstage.io/kubernetes-id'],
    optionalAnnotations: ['backstage.io/kubernetes-namespace', 'backstage.io/kubernetes-label-selector'],
    setupGuideUrl: '/docs/default/component/backstage/features/feature-discovery#kubernetes',
    exampleYaml: `metadata:
  annotations:
    backstage.io/kubernetes-id: my-service
    backstage.io/kubernetes-namespace: production`,
  },
  {
    id: 'datadog',
    name: 'Datadog',
    description: 'View APM metrics, traces, and dashboards',
    category: 'Integration',
    tabPath: '/datadog',
    requiredAnnotations: ['datadoghq.com/dashboard-url'],
    optionalAnnotations: ['datadoghq.com/service-name', 'datadoghq.com/env'],
    setupGuideUrl: '/docs/default/component/backstage/features/feature-discovery#datadog',
    exampleYaml: `metadata:
  annotations:
    datadoghq.com/dashboard-url: https://app.datadoghq.com/dashboard/abc-123
    datadoghq.com/service-name: my-service`,
  },

  // Development Features
  {
    id: 'pull-requests',
    name: 'Pull Requests',
    description: 'View open and recent pull requests',
    category: 'Development',
    tabPath: '/pull-requests',
    requiredAnnotations: ['github.com/project-slug'],
    setupGuideUrl: '/docs/default/component/backstage/features/feature-discovery#pull-requests',
    exampleYaml: `metadata:
  annotations:
    github.com/project-slug: your-org/your-repo`,
  },
  {
    id: 'github-issues',
    name: 'GitHub Issues',
    description: 'View and track GitHub issues',
    category: 'Development',
    tabPath: '/github-issues',
    requiredAnnotations: ['github.com/project-slug'],
    setupGuideUrl: '/docs/default/component/backstage/features/feature-discovery#github-issues',
    exampleYaml: `metadata:
  annotations:
    github.com/project-slug: your-org/your-repo`,
  },
  {
    id: 'code-insights',
    name: 'Code Insights',
    description: 'View GitHub contributors and README',
    category: 'Development',
    tabPath: '/code-insights',
    requiredAnnotations: ['github.com/project-slug'],
    setupGuideUrl: '/docs/default/component/backstage/features/feature-discovery#code-insights',
    exampleYaml: `metadata:
  annotations:
    github.com/project-slug: your-org/your-repo`,
  },
  {
    id: 'adrs',
    name: 'ADRs',
    description: 'Architecture Decision Records documentation',
    category: 'Development',
    tabPath: '/adrs',
    requiredAnnotations: ['backstage.io/adr-location'],
    setupGuideUrl: '/docs/default/component/backstage/features/feature-discovery#adrs',
    exampleYaml: `metadata:
  annotations:
    backstage.io/adr-location: docs/adr`,
  },

  // Operations Features
  {
    id: 'techdocs',
    name: 'TechDocs',
    description: 'View generated documentation',
    category: 'Operations',
    tabPath: '/docs',
    anyOfAnnotations: [
      'backstage.io/techdocs-ref',
      'backstage.io/techdocs-entity',
    ],
    setupGuideUrl: '/docs/default/component/backstage/features/feature-discovery#techdocs',
    exampleYaml: `metadata:
  annotations:
    backstage.io/techdocs-ref: dir:.
    # or point at another entity's published docs:
    # backstage.io/techdocs-entity: component:default/cloud-docs
    # backstage.io/techdocs-entity-path: /papi`,
  },
  {
    id: 'tech-radar',
    name: 'Tech Radar',
    description: 'View technology adoption status',
    category: 'Operations',
    tabPath: '/tech-radar',
    requiredAnnotations: ['tech-radar/id'],
    setupGuideUrl: '/docs/default/component/backstage/features/feature-discovery#tech-radar',
    exampleYaml: `metadata:
  annotations:
    tech-radar/id: my-tech-item`,
  },
  {
    id: 'score-card',
    name: 'Score Card',
    description: 'View entity quality scores and metrics',
    category: 'Operations',
    tabPath: '/score-card',
    requiredAnnotations: [],
    setupGuideUrl: '/docs/default/component/backstage/features/feature-discovery#score-card',
    exampleYaml: `# Score card is always available
# Scores are calculated based on entity metadata`,
  },
  {
    id: 'feedback',
    name: 'Feedback',
    description: 'Provide and view feedback, ratings, and reviews on entities',
    category: 'Operations',
    tabPath: '/feedback',
    requiredAnnotations: [],
    setupGuideUrl: '/docs/default/component/backstage/features/feature-discovery#feedback',
    exampleYaml: `# Feedback is always available
# Users can rate and provide feedback on any entity`,
  },
  {
    id: 'qeta',
    name: 'Q&A',
    description: 'Ask questions and get answers from the community',
    category: 'Operations',
    tabPath: '/qeta',
    requiredAnnotations: [],
    setupGuideUrl: '/docs/default/component/backstage/features/feature-discovery#qeta',
    exampleYaml: `# Q&A is always available
# Users can ask questions on any entity`,
  },

  // Quality Features
  {
    id: 'sonarqube',
    name: 'SonarQube',
    description: 'View code quality metrics and issues',
    category: 'Quality',
    requiredAnnotations: ['sonarqube.org/project-key'],
    setupGuideUrl: '/docs/default/component/backstage/features/feature-discovery#sonarqube',
    exampleYaml: `metadata:
  annotations:
    sonarqube.org/project-key: my-project`,
  },
];

/**
 * Feature Map for O(1) lookups by ID
 */
const FEATURE_MAP = new Map(FEATURE_DEFINITIONS.map(f => [f.id, f]));

/**
 * Get features by category
 */
export function getFeaturesByCategory(category: FeatureDefinition['category']): FeatureDefinition[] {
  return FEATURE_DEFINITIONS.filter(f => f.category === category);
}

/**
 * Get feature by ID (O(1) lookup using Map)
 */
export function getFeatureById(id: string): FeatureDefinition | undefined {
  return FEATURE_MAP.get(id);
}

/**
 * Get all categories (derived from feature definitions)
 */
export function getAllCategories(): FeatureDefinition['category'][] {
  const categories = new Set(FEATURE_DEFINITIONS.map(f => f.category));
  return Array.from(categories).sort();
}
